<script lang="ts">
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { fly, fade } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import {
    Brain,
    Clock,
    Zap,
    Award,
    ChevronRight,
    ChevronLeft,
    Check,
    Sparkles,
    BookOpen,
    Globe,
    Languages,
    Star,
  } from 'lucide-svelte';
  import Button from './ui/button.svelte';
  import Card from './ui/card.svelte';
  import { celebrate, successConfetti } from '../lib/confetti';
  import { hapticFeedback } from '../lib/animations';
  import { settingsActions, userSettings } from '../stores/settings';
  import { loadingOverlayActions } from '../stores/ui';
  import { userLanguagesActions } from '../stores/userLanguages';
  import { targetLanguages } from '../stores/flashcards';
  import { supabase } from '../lib/supabase';
  import { _ } from 'svelte-i18n';
  import { setAppLocale } from '../lib/i18n';
  import { getFlagUrl } from '../lib/languageFlags';

  let { onComplete }: { onComplete: () => void } = $props();

  // Load state from localStorage or userSettings
  const ONBOARDING_STORAGE_KEY = 'vocario_onboarding_state';

  function loadOnboardingState() {
    try {
      const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to load onboarding state:', e);
    }
    return null;
  }

  function saveOnboardingState() {
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify({
        selectedPreferredLanguage,
        selectedLanguageId,
        selectedProficiency,
      }));
    } catch (e) {
      console.error('Failed to save onboarding state:', e);
    }
  }

  const savedState = loadOnboardingState();

  let currentStep = $state(0);
  let isAnimating = $state(false);
  let isCompleting = $state(false);
  let selectedLanguageId = $state<string | null>(savedState?.selectedLanguageId ?? null);
  let selectedProficiency = $state<string | null>(savedState?.selectedProficiency ?? null);
  let selectedPreferredLanguage = $state<'cs' | 'en' | null>(
    savedState?.selectedPreferredLanguage ?? (get(userSettings)?.preferred_language as 'cs' | 'en' | undefined) ?? null
  );
  let availableLanguages = $state<any[]>([]);

  // Filter out preferred language from target language selection
  // and apply i18n translations for language names
  let filteredLanguages = $derived(
    availableLanguages
      .filter(lang => {
        // Map preferred language locale to language code (cs -> cs, en -> en)
        const preferredCode = selectedPreferredLanguage;
        // Don't show the preferred language in the target language list
        return lang.code !== preferredCode;
      })
      .map(lang => ({
        ...lang,
        // Use i18n translation if available, fallback to database name
        displayName: $_(`language.names.${lang.code}`) || lang.name
      }))
  );

  const proficiencyLevels = [
    {
      code: 'a1',
      titleKey: 'onboarding.levels.a1.title',
      descriptionKey: 'onboarding.levels.a1.description',
    },
    {
      code: 'a2',
      titleKey: 'onboarding.levels.a2.title',
      descriptionKey: 'onboarding.levels.a2.description',
    },
    {
      code: 'b1',
      titleKey: 'onboarding.levels.b1.title',
      descriptionKey: 'onboarding.levels.b1.description',
    },
    {
      code: 'b2',
      titleKey: 'onboarding.levels.b2.title',
      descriptionKey: 'onboarding.levels.b2.description',
    },
    {
      code: 'c1',
      titleKey: 'onboarding.levels.c1.title',
      descriptionKey: 'onboarding.levels.c1.description',
    },
    {
      code: 'c2',
      titleKey: 'onboarding.levels.c2.title',
      descriptionKey: 'onboarding.levels.c2.description',
    },
  ];

  // Removed: Now using centralized getFlagUrl from lib/languageFlags

  const preferredLanguageOptions = [
    { code: 'cz', locale: 'cs' as const, labelKey: 'language.names.cs' },
    { code: 'gb', locale: 'en' as const, labelKey: 'language.names.en' },
  ];

  const steps = [
    {
      id: 'welcome',
      titleKey: 'onboarding.steps.welcome.title',
      subtitleKey: 'onboarding.steps.welcome.subtitle',
      descriptionKey: 'onboarding.steps.welcome.description',
      icon: Sparkles,
      color: 'text-purple-200',
      bgColor: 'bg-purple-200/10',
    },
    {
      id: 'language-selection',
      titleKey: 'onboarding.steps.language_selection.title',
      subtitleKey: 'onboarding.steps.language_selection.subtitle',
      descriptionKey: 'onboarding.steps.language_selection.description',
      icon: Languages,
      color: 'text-purple-200',
      bgColor: 'bg-purple-200/10',
    },
    {
      id: 'proficiency-selection',
      titleKey: 'onboarding.steps.proficiency.title',
      subtitleKey: 'onboarding.steps.proficiency.subtitle',
      descriptionKey: 'onboarding.steps.proficiency.description',
      icon: Star,
      color: 'text-purple-200',
      bgColor: 'bg-purple-200/10',
    },
    {
      id: 'efficiency',
      titleKey: 'onboarding.steps.efficiency.title',
      subtitleKey: 'onboarding.steps.efficiency.subtitle',
      descriptionKey: 'onboarding.steps.efficiency.description',
      icon: Zap,
      color: 'text-purple-200',
      bgColor: 'bg-purple-200/10',
    },
    {
      id: 'features',
      titleKey: 'onboarding.steps.features.title',
      subtitleKey: 'onboarding.steps.features.subtitle',
      icon: Award,
      color: 'text-purple-200',
      bgColor: 'bg-purple-200/10',
      features: [
        { icon: BookOpen, textKey: 'onboarding.features.unlimited_cards' },
        { icon: Globe, textKey: 'onboarding.features.multi_language' },
        { icon: Clock, textKey: 'onboarding.features.offline_mode' },
        { icon: Brain, textKey: 'onboarding.features.ai_tools' },
      ],
    },
    {
      id: 'ready',
      titleKey: 'onboarding.steps.ready.title',
      subtitleKey: 'onboarding.steps.ready.subtitle',
      descriptionKey: 'onboarding.steps.ready.description',
      icon: Check,
      color: 'text-purple-200',
      bgColor: 'bg-purple-200/10',
    },
  ];

  let currentStepData = $derived(steps[currentStep]);
  let progress = $derived(((currentStep + 1) / steps.length) * 100);

  async function nextStep() {
    if (isAnimating || isCompleting) return;

    // Validate preferred language selection on welcome step
    if (currentStep === 0 && !selectedPreferredLanguage) {
      alert($_('onboarding.validation.select_preferred_language'));
      return;
    }

    // Validate language selection on language-selection step
    if (currentStep === 1 && !selectedLanguageId) {
      alert($_('onboarding.validation.select_target_language'));
      return;
    }

    // Validate proficiency selection on proficiency-selection step
    if (currentStep === 2 && !selectedProficiency) {
      alert($_('onboarding.validation.select_proficiency'));
      return;
    }

    // Save state to localStorage
    saveOnboardingState();

    await hapticFeedback('light');

    if (currentStep < steps.length - 1) {
      isAnimating = true;
      currentStep++;
      setTimeout(() => (isAnimating = false), 300);

      // Celebrate on last step
      if (currentStep === steps.length - 1) {
        setTimeout(() => celebrate(), 300);
      }
    } else {
      // Complete onboarding
      await completeOnboarding();
    }
  }

  async function prevStep() {
    if (isAnimating || isCompleting || currentStep === 0) return;

    await hapticFeedback('light');

    isAnimating = true;
    currentStep--;
    setTimeout(() => (isAnimating = false), 300);
  }

  async function skipOnboarding() {
    if (isCompleting) return;
    await hapticFeedback('medium');
    await completeOnboarding();
  }

  async function completeOnboarding() {
    if (isCompleting) return;

    isCompleting = true;

    await hapticFeedback('heavy');
    successConfetti();

    loadingOverlayActions.show($_('onboarding.status.preparing'));

    try {
      // Save preferred language
      if (selectedPreferredLanguage) {
        await settingsActions.updateSetting('preferred_language', selectedPreferredLanguage);
        setAppLocale(selectedPreferredLanguage);
      }

      // Add selected language for the user
      if (selectedLanguageId) {
        try {
          await userLanguagesActions.addLanguage(selectedLanguageId, true);
        } catch (error) {
          // If language already exists, that's fine - just continue
          if (error instanceof Error && error.message !== 'Language already added') {
            throw error;
          }
          console.log('Language already exists, continuing...');
        }
      }

      // Import flashcards based on selected language and proficiency
      if (selectedLanguageId && selectedProficiency) {
        loadingOverlayActions.show($_('onboarding.status.importing'));
        
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error('User not authenticated');

          // Get selected language details
          const selectedLang = availableLanguages.find(l => l.id === selectedLanguageId);
          if (!selectedLang) throw new Error('Selected language not found');

          // Construct CSV filename: {primary}-{target}-{level}.csv
          const primaryLanguage = selectedPreferredLanguage ?? get(userSettings)?.preferred_language ?? 'cs';
          const csvFilename = `${primaryLanguage.toLowerCase()}-${selectedLang.code.toLowerCase()}-${selectedProficiency.toLowerCase()}.csv`;
          const csvUrl = `/imports/${csvFilename}`;

          // Fetch CSV file
          const csvResponse = await fetch(csvUrl);
          if (!csvResponse.ok) {
            console.log(`No import file available: ${csvFilename}`);
            return;
          }

          const csvText = await csvResponse.text();
          
          // Parse CSV manually (simple parser for header + rows)
          const lines = csvText.trim().split('\n');
          if (lines.length < 2) {
            console.log('CSV file is empty');
            return;
          }

          const headers = lines[0].split(',').map(h => h.trim());
          const primaryLangIdx = headers.indexOf('primary_text');
          const targetLangIdx = headers.indexOf('target_text');
          const transliterationIdx = headers.indexOf('transliteration');
          const phoneticIdx = headers.indexOf('phonetic');
          const categoryIdx = headers.indexOf('category_name');

          if (primaryLangIdx === -1 || targetLangIdx === -1) {
            throw new Error('Invalid CSV format: missing primary_text or target_text');
          }

          // Get target language ID from database
          const { data: targetLangData, error: langError } = await supabase
            .from('flashcard_target_languages')
            .select('id')
            .eq('code', selectedLang.code)
            .single();

          if (langError || !targetLangData) {
            throw new Error(`Target language not found: ${selectedLang.code}`);
          }

          const targetLanguageId = targetLangData.id;
          const categoryMap = new Map<string, string>();
          const flashcardsToInsert = [];

          // Fetch all existing categories for this target language to avoid individual queries
          const { data: existingCategories = [] } = await supabase
            .from('flashcard_categories')
            .select('id, name')
            .eq('target_language_id', targetLanguageId)
            .eq('user_id', user.id);

          // Build category map from existing categories
          existingCategories.forEach(cat => {
            categoryMap.set(cat.name, cat.id);
          });

          // Get unique category names from CSV
          const categoriesToCreate = new Set<string>();
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            const values = [];
            let current = '';
            let inQuotes = false;

            for (let j = 0; j < line.length; j++) {
              const char = line[j];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                values.push(current.trim().replace(/^"|"$/g, ''));
                current = '';
              } else {
                current += char;
              }
            }
            values.push(current.trim().replace(/^"|"$/g, ''));

            const categoryName = values[categoryIdx]?.trim();
            if (categoryName && !categoryMap.has(categoryName)) {
              categoriesToCreate.add(categoryName);
            }
          }

          // Create missing categories in batch
          if (categoriesToCreate.size > 0) {
            const newCategories = Array.from(categoriesToCreate).map(name => ({
              name,
              target_language_id: targetLanguageId,
              user_id: user.id,
            }));

            const { data: createdCategories } = await supabase
              .from('flashcard_categories')
              .insert(newCategories)
              .select('id, name');

            if (createdCategories) {
              createdCategories.forEach(cat => {
                categoryMap.set(cat.name, cat.id);
              });
            }
          }

          // Process CSV rows and build flashcards
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            // Simple CSV parsing (handles quoted values)
            const values = [];
            let current = '';
            let inQuotes = false;

            for (let j = 0; j < line.length; j++) {
              const char = line[j];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                values.push(current.trim().replace(/^"|"$/g, ''));
                current = '';
              } else {
                current += char;
              }
            }
            values.push(current.trim().replace(/^"|"$/g, ''));

            const primaryText = values[primaryLangIdx]?.trim();
            const targetText = values[targetLangIdx]?.trim();
            const transliteration = values[transliterationIdx]?.trim();
            const phonetic = values[phoneticIdx]?.trim();
            const categoryName = values[categoryIdx]?.trim();

            if (!primaryText || !targetText) continue;

            const categoryId = categoryName ? categoryMap.get(categoryName) ?? null : null;

            flashcardsToInsert.push({
              user_id: user.id,
              primary_text: primaryText,
              target_text: targetText,
              transliteration: transliteration || null,
              phonetic: phonetic || null,
              level: selectedProficiency,
              target_language_id: targetLanguageId,
              category_id: categoryId,
              is_learned: false,
              examples: [],
            });
          }

          // Insert flashcards in batch
          if (flashcardsToInsert.length > 0) {
            const { error: insertError } = await supabase.from('flashcards').insert(flashcardsToInsert);

            if (insertError) {
              console.error('Error inserting flashcards:', insertError);
              throw insertError;
            }

            console.log(`Imported ${flashcardsToInsert.length} flashcards`);
          } else {
            console.log('No flashcards to import from CSV');
          }
        } catch (importError) {
          // Don't block onboarding if import fails
          console.error('Flashcard import error:', importError);
        }
      }

      // Mark onboarding as completed
      await settingsActions.updateSetting('onboarding_completed', true);

      // Clear onboarding state from localStorage
      try {
        localStorage.removeItem(ONBOARDING_STORAGE_KEY);
      } catch (e) {
        console.error('Failed to clear onboarding state:', e);
      }

      await new Promise<void>((resolve) => setTimeout(resolve, 500));
      onComplete();
    } catch (error) {
      console.error('Onboarding completion failed:', error);
    } finally {
      loadingOverlayActions.hide();
      isCompleting = false;
    }
  }

  onMount(async () => {
    // Trigger initial haptic
    hapticFeedback('light');

    // Load available languages
    try {
      const { data, error } = await supabase
        .from('flashcard_target_languages')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      availableLanguages = data || [];
    } catch (error) {
      console.error('Failed to load languages:', error);
    }
  });
</script>

<div class="min-h-screen bg-gradient-primary flex flex-col">
  <!-- Progress bar -->
  <div class="fixed top-0 left-0 right-0 h-1 bg-white/20 z-50">
    <div
      class="h-full bg-white transition-all duration-500 ease-out"
      style="width: {progress}%"
    ></div>
  </div>

  <!-- Skip button -->
  {#if currentStep < steps.length - 1}
    <button
      onclick={() => skipOnboarding()}
      disabled={isCompleting}
      class="fixed top-4 right-4 hover:text-white text-sm font-medium transition-colors z-40"
    >
      {$_('onboarding.actions.skip')}
    </button>
  {/if}

  <!-- Main content -->
  <div class="flex-1 flex items-center justify-center px-6 py-12">
    {#key currentStep}
      {@const IconComponent = currentStepData.icon}
      <div
        in:fly={{ x: 50, duration: 400, easing: cubicOut }}
        out:fly={{ x: -50, duration: 300, easing: cubicOut }}
        class="w-full max-w-md"
      >
        <Card class="bg-white/10 backdrop-blur-lg border-white/20 p-8">
          <!-- Icon -->
          <div
            class="w-20 h-20 rounded-full {currentStepData.bgColor} flex items-center justify-center mx-auto mb-6"
          >
            <IconComponent class="w-10 h-10 {currentStepData.color}" />
          </div>

          <!-- Title -->
          <h1 class="text-3xl font-bold text-center mb-3">
            {$_(currentStepData.titleKey)}
          </h1>

          <!-- Subtitle -->
          <p class="text-lg text-center mb-6">
            {$_(currentStepData.subtitleKey)}
          </p>

          <!-- Description -->
          {#if currentStepData.descriptionKey}
            <p class="text-center mb-6">
              {$_(currentStepData.descriptionKey)}
            </p>
          {/if}

          <!-- Preferred language selection (step 0) -->
          {#if currentStep === 0}
            <div class="mb-6 grid grid-cols-2 gap-4">
              {#each preferredLanguageOptions as lang}
                <button
                  type="button"
                  onclick={() => {
                    selectedPreferredLanguage = lang.locale;
                    setAppLocale(lang.locale);
                    saveOnboardingState();
                    hapticFeedback('light');
                  }}
                  class="flex flex-col items-center p-6 rounded-xl border-2 transition-all duration-200 {selectedPreferredLanguage === lang.locale
                    ? 'border-white bg-white/20 shadow-lg scale-105'
                    : 'border-white/30 bg-white/5 hover:bg-white/10 hover:border-white/50'}"
                >
                  <div class="h-16 w-20 rounded-lg overflow-hidden shadow-soft-sm mb-3">
                    <img
                      src={`https://flagcdn.com/${lang.code}.svg`}
                      alt={$_(lang.labelKey)}
                      class="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div class="text-sm font-medium text-center">{$_(lang.labelKey)}</div>
                </button>
              {/each}
            </div>
          {/if}

          <!-- Language selection (step 1) -->
          {#if currentStep === 1}
            <div class="mb-6 max-h-80 overflow-y-auto">
              <div class="grid grid-cols-2 gap-3 p-3">
                {#each filteredLanguages as language}
                  <button
                    type="button"
                    onclick={() => {
                      selectedLanguageId = language.id;
                      saveOnboardingState();
                      hapticFeedback('light');
                    }}
                    class="flex flex-col items-center p-4 rounded-xl border-2 transition-all duration-200 {selectedLanguageId === language.id
                      ? 'border-white bg-white/20 shadow-lg scale-105'
                      : 'border-white/30 bg-white/5 hover:bg-white/10 hover:border-white/50'}"
                  >
                    <div class="h-12 w-16 rounded-lg overflow-hidden shadow-soft-sm mb-2">
                      <img
                        src={getFlagUrl(language.code)}
                        alt={language.displayName}
                        class="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div class="text-sm font-medium text-center">{language.displayName}</div>
                  </button>
                {/each}
              </div>
            </div>
          {/if}

          <!-- Proficiency selection (step 2) -->
          {#if currentStep === 2}
            <div class="mb-6 max-h-80 overflow-y-auto">
              <div class="space-y-3">
                {#each proficiencyLevels as level}
                  <button
                    type="button"
                    onclick={() => {
                      selectedProficiency = level.code;
                      saveOnboardingState();
                      hapticFeedback('light');
                    }}
                    class="w-full p-4 rounded-xl border-2 transition-all duration-200 text-left {selectedProficiency === level.code
                      ? 'border-white bg-white/20 shadow-lg'
                      : 'border-white/30 bg-white/5 hover:bg-white/10 hover:border-white/50'}"
                  >
                    <div class="font-semibold mb-1">{$_(level.titleKey)}</div>
                    <div class="text-sm">{$_(level.descriptionKey)}</div>
                  </button>
                {/each}
              </div>
            </div>
          {/if}

          <!-- Features list -->
          {#if currentStepData.features}
            <div class="grid grid-cols-2 gap-4 mb-6">
              {#each currentStepData.features as feature, i}
                {@const FeatureIcon = feature.icon}
                <div
                  in:fly={{ y: 20, delay: i * 100, duration: 400 }}
                  class="flex flex-col items-center gap-2 p-4 rounded-lg bg-white/5"
                >
                  <FeatureIcon class="w-8 h-8" />
                  <span class="text-sm text-center">{$_(feature.textKey)}</span>
                </div>
              {/each}
            </div>
          {/if}

          <!-- Navigation buttons -->
          <div class="flex gap-3 mt-8">
            {#if currentStep > 0}
              <Button
                onclick={() => prevStep()}
                variant="outline"
                class="flex-1 bg-white/10 border-white/30 hover:bg-white/20"
              >
                <ChevronLeft class="w-5 h-5 mr-2" />
                {$_('navigation.back')}
              </Button>
            {/if}

            <Button
              onclick={() => nextStep()}
              disabled={isCompleting}
              class="flex-1 bg-white text-gray-900 hover:bg-white/90 font-semibold"
            >
              {#if currentStep === steps.length - 1}
                {#if isCompleting}
                  <span class="flex w-full items-center justify-center gap-2">
                    <span class="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" aria-hidden="true"></span>
                    <span>{$_('messages.loading')}</span>
                  </span>
                {:else}
                  {$_('buttons.start_study')}
                  <Check class="w-5 h-5 ml-2" />
                {/if}
              {:else}
                {$_('buttons.next')}
                <ChevronRight class="w-5 h-5 ml-2" />
              {/if}
            </Button>
          </div>

          <!-- Step indicators -->
          <div class="flex justify-center gap-2 mt-6">
            {#each steps as _, i}
              <div
                class="h-2 rounded-full transition-all duration-300 {i === currentStep ? 'w-8 bg-white' : 'w-2 bg-white/30'}"
              ></div>
            {/each}
          </div>
        </Card>
      </div>
    {/key}
  </div>
</div>
